import React from 'react';

import { Area, Column } from '@ant-design/charts';

import type { ChartView } from '../types';
import styles from '../charts.module.less';

export interface PercentTrendDatum {
  period: string;
  /** Percentage 0–100; `null` when the period has nothing to rate (no bar is drawn). */
  value: number | null;
  completed: number;
  total: number;
  color: string;
}

export interface PercentTrendChartProps {
  data: PercentTrendDatum[];
  view: ChartView;
  tooltipLabel: string;
  /** Secondary tooltip text for a point, e.g. "8 of 10 completed". */
  formatTooltipDetail: (datum: PercentTrendDatum) => string;
  /** Tooltip text for a period without a value. */
  emptyLabel?: string;
  containerRef?: React.Ref<HTMLDivElement>;
  height?: number;
  lineColor?: string;
}

const APPEND_PADDING = [36, 28, 24, 28];

/** Percentage per period as colored columns or as a single area line. */
const PercentTrendChart: React.FC<PercentTrendChartProps> = ({
  data,
  view,
  tooltipLabel,
  formatTooltipDetail,
  emptyLabel = '',
  containerRef,
  height = 250,
  lineColor = '#3F8DD8',
}) => {
  const tooltip = {
    fields: ['period', 'value', 'completed', 'total'],
    formatter: (datum: { period: string; value: number | null }) => {
      const point = data.find(item => item.period === datum.period);
      if (!point || point.value === null) {
        return { name: tooltipLabel, value: point ? emptyLabel : `${datum.value ?? ''}%` };
      }
      return { name: tooltipLabel, value: `${point.value}% · ${formatTooltipDetail(point)}` };
    },
  };
  const commonConfig = {
    data,
    xField: 'period',
    yField: 'value',
    height,
    appendPadding: APPEND_PADDING,
    xAxis: { label: { autoHide: true, autoRotate: false } },
    yAxis: { max: 100 },
    tooltip,
    legend: false as const,
    animation: false,
  };

  return (
    <div ref={containerRef} className={styles.chart}>
      {view === 'bar' ? (
        <Column
          {...commonConfig}
          seriesField="color"
          color={(datum: { color: string }) => datum.color}
          columnWidthRatio={0.72}
          maxColumnWidth={72}
          columnStyle={{ radius: [3, 3, 0, 0] }}
          label={{
            position: 'top',
            content: (datum: { value: number | null }) => (datum.value === null ? '' : `${datum.value}%`),
          }}
        />
      ) : (
        <Area
          {...commonConfig}
          // Keep empty periods on the baseline so the line spans the complete timeline.
          data={data.map(datum => ({ ...datum, value: datum.value ?? 0 }))}
          color={lineColor}
          areaStyle={{
            fill: `l(270) 0:${lineColor} 1:rgba(255,255,255,0)`,
            fillOpacity: 0.24,
          }}
          line={{ size: 2.5, style: { lineCap: 'round', lineJoin: 'round' } }}
          point={{ size: 4, shape: 'circle' }}
        />
      )}
    </div>
  );
};

export default PercentTrendChart;
